import {
  AfterContentChecked,
  Component,
  OnInit,
  OnDestroy,
  signal,
  computed,
  SimpleChanges,
  OnChanges,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  input,
  output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { Store } from '@ngrx/store';
import { ReplaySubject, filter, firstValueFrom, pairwise, startWith, takeUntil } from 'rxjs';

import { CustomRoomTutorial, LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { AuthService } from 'app/services/auth.service';
import assertDefined from 'app/utils/assert-defined';
import { HelpCenterUrls } from 'app/utils/help-center';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import Social from 'entity/Social';
import StartingPoint from 'entity/StartingPoint';
import User from 'entity/User';
import UserController from 'rest/UserController';

import { NavigationModalComponent, NavigationModalData } from '../../navigation-modal/navigation-modal.component';
import { TUTORIAL_CARDS, TutorialCard } from '../../room-tutorial/room-tutorial.types';
import { BadgingService } from '../../services/badging/badging.service';
import { BrowserService } from '../../services/browser.service';
import { ConnectedRoomsService } from '../../services/connected-rooms.service';
import { DeviceService } from '../../services/device/device.service';
import { DevicePermissionService } from '../../services/device-permission/device-permission.service';
import { MultimediaUserService } from '../../services/multimedia-user.service';
import { PermissionsService } from '../../services/permissions.service';
import { RoomPresenceService } from '../../services/room/room-presence.service';
import { SceneService } from '../../services/scene.service';
import { VideoChatService } from '../../services/videochat.service';
import { WebsocketService } from '../../services/websocket.service';
import { ConnectedRoomCounts, actions, socialAppSelectors } from '../../state/app';
import { mediaActions } from '../../state/media';
import { EventsService } from 'app/modules/social-events/services/events.service';
import { Router } from '@angular/router';
import SocialEvent from 'entity/SocialEvent';
import { isReadable } from '@ctrl/tinycolor';

const DEFAULT_BACKGROUND_COLOR = '#0f2c6a';
const DEFAULT_DIV_BACKGROUND_COLOR = '#ffffff1a';
const OPTIONAL_DIV_BACKGROUND_COLOR = '#091d50';
@Component({
  selector: 'app-welcome-wrapper',
  templateUrl: './welcome-wrapper.component.html',
  styleUrls: ['./welcome-wrapper.component.scss'],
  providers: [{ provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } }],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WelcomeWrapperComponent extends Loggable implements OnInit, AfterContentChecked, OnDestroy, OnChanges {
  private dialog = inject(MatDialog);
  private videoChatService = inject(VideoChatService);
  private deviceService = inject(DeviceService);
  private browserService = inject(BrowserService);
  private multimediaUserService = inject(MultimediaUserService);
  private roomPresenceService = inject(RoomPresenceService);
  private devicePermissionService = inject(DevicePermissionService);
  private connectedRoomsService = inject(ConnectedRoomsService);
  private permissionsService = inject(PermissionsService);
  private websocketService = inject(WebsocketService);
  private badgingService = inject(BadgingService);
  private authService = inject(AuthService);
  private store = inject(Store);
  private sceneService = inject(SceneService);
  private eventsService = inject(EventsService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);

  public readonly waitingForHostStep = input<string>();
  public readonly user = input<User>();
  public readonly event = input<SocialEvent>();
  public readonly joinSocial = output<Social>();
  public readonly hostAdmissionRequired = output<Social>();
  public readonly triggerTransitionOverlay = output<boolean>();

  private didClickJoinNowTimeout: NodeJS.Timeout | undefined;

  private destroyed$ = new ReplaySubject<boolean>(1);

  // Social variables
  public readonly connectedRoomSet = signal<Social[]>([]);
  private connectedRoomCounts: ConnectedRoomCounts;
  private audioElement!: HTMLAudioElement;

  protected readonly social = signal<Social | undefined>(undefined);
  protected readonly themeBackgroundColor = computed(
    () => this.social()?.theme?.colorBackground ?? DEFAULT_BACKGROUND_COLOR,
  );
  protected readonly backgroundDivColor = computed(() =>
    isReadable(DEFAULT_DIV_BACKGROUND_COLOR, this.themeBackgroundColor())
      ? DEFAULT_DIV_BACKGROUND_COLOR
      : OPTIONAL_DIV_BACKGROUND_COLOR,
  );

  // Banner and display variables
  public useDarkTheme = false;
  public displayWarning = false;
  public displayEarlyAccess = false;

  // Flow control and flags
  public startingPoint: StartingPoint | undefined;
  public readonly selectedRoomFull = signal(false);
  public socialStarted = false;
  public roomIsOpen = false;
  public isHost = false;
  private readonly isTestUser = signal(false);
  public readonly didClickJoinNow = signal(false);
  private isIntroVideoDisabled = false;
  public readonly shouldShow = signal<boolean>(true);
  public readonly showLoader = signal<boolean>(false);
  private readonly serviceCanUserJoin = computed(() => this.devicePermissionService.canUserJoin());

  protected readonly canUserJoin = computed(() => {
    if (this.isTestUser()) {
      return true;
    }
    if (!this.social()) {
      return false;
    }
    if (this.didClickJoinNow()) {
      return false;
    }
    // Checks if room is at max participants and user is not host
    if (this.selectedRoomFull()) {
      return false;
    }
    if (!this.serviceCanUserJoin()) {
      return false;
    }

    return true;
  });

  //Title variables
  protected ownersName?: string;
  protected roomsName?: string;

  //Tutorial variables
  private useNewTutorial = false;
  protected readonly showTutorial = signal<boolean>(false);
  private customRoomTutorial?: CustomRoomTutorial;
  public tutorialCards: TutorialCard[] = [...TUTORIAL_CARDS];
  protected readonly demoCompleted = signal<boolean>(false);

  public readonly showWelcomeDeviceSelectionForm = signal<boolean>(true);

  // Badging Feature Flag
  protected readonly showSmartBadging = signal<boolean>(false);
  protected readonly smartBadgingHasQuestions = signal<boolean>(false);
  protected socialRoomSetId = '';

  // Onboarding experience
  protected readonly onboardingExperience = signal<boolean>(false);
  protected readonly showOnboardingExperience = signal<boolean>(false);
  protected readonly shouldContinueFromSmartBadging = signal<boolean>(false);
  protected readonly showOrientationStep = signal<boolean>(false);
  protected readonly badgeQuestionsStep = signal<boolean>(false);
  private readonly triggerOnboardingExperience = signal<boolean>(false);

  // Events countdown page
  protected readonly eventsCountdown = signal<boolean>(false);
  protected readonly isMobile = signal<boolean>(false);

  protected scootaverse: Social | undefined;
  private readonly eventFlowStep = signal<string | undefined>(undefined);
  private readonly currentFlowStep = signal<string | undefined>(undefined);
  private hasWaitingHostStep?: boolean;

  protected badgingHelpURL = HelpCenterUrls.SMART_BADGING;

  public constructor() {
    super();
    this.setDisplayName('WelcomeWrapperComponent');
    this.store
      .select(socialAppSelectors.getCurrentSocial)
      .pipe(startWith({} as Social), pairwise(), filter(notNil), takeUntil(this.destroyed$))
      .subscribe(([, currentSocial]) => {
        this.social.set(currentSocial);
        this.roomIsOpen = !!currentSocial?.isOpen;
        this.roomsName = this.social()?.name;
        this.cdr.markForCheck();
      });

    this.store
      .select(socialAppSelectors.getCurrentConnectedRoomSet)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((connectedRoomSet) => {
        this.scootaverse = connectedRoomSet;
        this.connectedRoomSet.set(connectedRoomSet.socials || []);
        this.cdr.markForCheck();
      });

    this.store
      .select(socialAppSelectors.isHost)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isHost) => {
        this.isHost = isHost;
        this.cdr.markForCheck();
      });

    this.store
      .select(coreSelectors.isTestUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isTestUser) => {
        this.isTestUser.set(isTestUser);
        this.cdr.markForCheck();
      });

    this.store
      .select(socialAppSelectors.getCurrentConnectedRoomCounts)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((updatedCounts: ConnectedRoomCounts) => {
        this.connectedRoomCounts = updatedCounts;
        this.cdr.markForCheck();
      });

    // Test flag to trigger onboarding experience flow
    this.store
      .select(socialAppSelectors.showOnboardingExperience)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((triggerOnboardingExperience) => this.triggerOnboardingExperience.set(triggerOnboardingExperience));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_disable_intro_video)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isIntroVideoDisabled) => {
        this.isIntroVideoDisabled = isIntroVideoDisabled;
        this.cdr.markForCheck();
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.smart_badging)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((showSmartBadging) => {
        this.showSmartBadging.set(showSmartBadging);
      });

    /** tutorial flags */
    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_new_tutorial)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((useNewTutorial) => {
        this.useNewTutorial = useNewTutorial;
        this.cdr.markForCheck();
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.custom_room_tutorial)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((customRoomTutorial) => {
        this.customRoomTutorial = customRoomTutorial;
        this.cdr.markForCheck();
      });

    // Onboarding experience flag
    getFeatureFlagObservable(this.store, LaunchDarklyFlags.onboarding_experience)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((onboarding_experience) => {
        this.onboardingExperience.set(onboarding_experience);
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.onboarding_experience_override)
      .pipe(
        filter((onboarding_experience_override) => onboarding_experience_override),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        this.store.dispatch(actions.SHOW_ONBOARDING_EXPERIENCE({ showOnboardingExperience: true }));
        this.cdr.markForCheck();
      });

    // Events countdown page flag
    getFeatureFlagObservable(this.store, LaunchDarklyFlags.events_countdown)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(async (events_countdown) => {
        this.eventsCountdown.set(events_countdown);

        if (events_countdown) {
          // Handle specific step in countdown page
          this.eventFlowStep.set(this.eventsService.eventFlowStep);
          if (this.eventFlowStep()) {
            await this.handleWelcomeFlowStep(this.eventFlowStep());
          }
        }
        this.cdr.markForCheck();
      });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.waitingForHostStep && changes.waitingForHostStep.currentValue) {
      this.hasWaitingHostStep = true;
      this.shouldShow.set(true);
      this.currentFlowStep.set(changes.waitingForHostStep.currentValue);
      void this.handleWelcomeFlowStep(changes.waitingForHostStep.currentValue);
    }
  }

  // Even though Angular lifecycle methods are typically not async,
  // we need async here due to the multiple async operations
  public async ngOnInit(): Promise<void> {
    //Init title variables
    this.ownersName = this.social()?.user?.firstName;
    this.roomsName = this.social()?.name;
    this.socialRoomSetId = this.social()?.socialRoomSetId ?? '';

    // Ensure default devices are set if available
    this.ensureDefaultDevicesAreSet();

    this.isMobile.set(this.browserService.isMobile());

    const [hasCompletedDemo, check] = await Promise.all([
      firstValueFrom(this.videoChatService.hasCompletedDemo()),
      this.browserService.checkBrowserAvailability(),
    ]);
    this.demoCompleted.set(hasCompletedDemo);

    this.displayWarning = check.isAvailable && check.showWarning;
    this.displayEarlyAccess = check.isAvailable && check.earlyAccess;

    if (this.social()?.theme?.socialThemeName === 'Celebration') {
      // This is sort of a hard coded hack for the one dark theme we have since it does not look good on this modal.
      this.useDarkTheme = true;
    }

    // Reinit media state
    // Do not carry over mute state from previous room
    this.multimediaUserService.reinitMediaState();

    // Check if browser or device are available
    try {
      await this.deviceService.checkBrowserAndDevice(check);
    } catch (error) {
      this.error('Failed to Check Browser and Device with error: ', error);
    }

    if (this.social()?.isOpen) {
      const socialId = this.social()?.socialId;
      if (!socialId) {
        this.warn('Could not get socialId');
        return;
      }

      const checkAdmittedStatus = await this.videoChatService.checkAdmittedStatus(socialId, false);
      if (checkAdmittedStatus.banned) {
        this.roomPresenceService.handleBannedUser();
        return;
      }
    }

    if (check.isAvailable) {
      try {
        // Initialize permission variables and subscription
        await this.devicePermissionService.initPermissions();
      } catch (error) {
        this.error('Failed to Initialize Permissions with error: ', error);
      }
    }

    if (this.showSmartBadging()) {
      this.checkBadgeHasQuestions().catch((e) => {
        this.error('Failed to get badge questions', e);
      });
    }

    this.openWebsocketConnection();

    // Log ChurnZero Event Enter Room when waiting room initializes
    // this.churnZeroService.trackEvent(ChurnZeroEvent.ENTER_ROOM);
  }

  private async checkBadgeHasQuestions(): Promise<void> {
    // Check if there are questions
    const badgeQuestions = await this.badgingService.getMyBadgeData(this.socialRoomSetId);
    this.smartBadgingHasQuestions.set(!!badgeQuestions.length);
    this.store.dispatch(actions.UPDATE_HAS_BADGE_QUESTIONS({ hasBadgeQuestions: !!badgeQuestions.length }));
  }

  /** TUTORIAL FUNCTIONS  */
  /** Complete tutorial */
  protected async onTutorialCompleted(): Promise<void> {
    const social = this.social();
    if (!social) {
      return;
    }
    this.showLoader.set(true);
    try {
      await firstValueFrom(this.videoChatService.setDemoCompleted());
      this.showTutorial.set(false);
      this.showLoader.set(false);
      await this.startJoinSocial(social);
    } catch (e) {
      this.error('Failed to complete tutorial', e);
    }
  }

  private showRoomTutorialVideo(src?: string, title = 'Host to Mingle', subtitle?: string): void {
    const data: NavigationModalData = { modalTitle: title, src, subtitle };
    const dialogRef = this.dialog.open<NavigationModalComponent, NavigationModalData>(NavigationModalComponent, {
      width: '540px',
      panelClass: 'social-modal',
      data,
      disableClose: true,
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.onTutorialCompleted());
  }

  /**
   * Open websocket connection
   */
  private openWebsocketConnection(): void {
    const meta = this.social()?.socialMeta ?? {};
    const { peerConnectionURL } = meta;

    if (!peerConnectionURL) {
      this.fatal('Did not receive url in social meta', { social: this.social() });
      return;
    }

    this.websocketService.connect(peerConnectionURL);
  }

  /** Start join social room */
  private async startJoinSocial(_social: Social): Promise<void> {
    const region = await this.videoChatService.getRegion();
    assertDefined(_social?.socialId);
    const social = await this.videoChatService?.getSocialPromise(_social.socialId, true, region);
    this.store.dispatch(actions.LOAD_SOCIAL({ social }));

    this.info(
      `Did Join Social with Mic ${this.devicePermissionService.selectedMic()?.label}, Camera ${this.devicePermissionService.selectedCamera()?.label}, and Speaker ${this.devicePermissionService.selectedSpeaker()?.label}`,
    );
    this.socialStarted = true;
    this.shouldShow.set(false);

    const { socialId } = social;
    if (!socialId) {
      this.error('startJoinSocial has an invalid social id');
      this.shouldShow.set(true);
      return;
    }
    const peerConnectionURL = social.socialMeta?.peerConnectionURL;
    if (peerConnectionURL !== this.websocketService.url) {
      this.info('Websocket URL has changed, closing old connection');
      await this.websocketService.close();
    }

    if (this.roomIsOpen) {
      const checkAdmittedStatus = await this.videoChatService.checkAdmittedStatus(socialId, false);
      if (checkAdmittedStatus.banned) {
        this.roomPresenceService.handleBannedUser();
        return;
      }
    }

    if (this.isHost) {
      await this.updateHostAttributes();
    }

    if (social.hostAdmitted && !this.isHost) {
      this.hostAdmissionRequired.emit(social);
    } else {
      this.sceneService.sceneManager?.runIfPixiInitialized(() => this.joinSocial.emit(social));
    }
    void this.permissionsService.cleanAllStreams();
    this.cleanDeviceDetection();

    this.didClickJoinNowTimeout = setTimeout(() => {
      this.didClickJoinNow.set(false);
    }, 1000);
  }

  protected async continueBehavior(badgeQuestionsSubmit?: boolean): Promise<void> {
    if ((this.eventsCountdown() && this.eventFlowStep() === 'badge') || this.eventFlowStep() === 'permissions') {
      this.eventsService.eventFlowStep = undefined;
      await this.redirectToCountdownPage();
    }

    if (this.hasWaitingHostStep) {
      this.shouldShow.set(false);
      this.triggerTransitionOverlay.emit(true);
      return;
    }

    if (!this.demoCompleted() || this.triggerOnboardingExperience()) {
      this.showTutorial.set(true);
    }

    if (this.onboardingExperience() && !this.showOnboardingExperience() && this.showTutorial() && !this.isMobile()) {
      this.showOnboardingExperience.set(true);
      localStorage.setItem('showOnboardingMessage', 'true');
      this.clearAudioElement();
      return;
    }

    if (this.showSmartBadging() && this.smartBadgingHasQuestions() && !badgeQuestionsSubmit) {
      this.showWelcomeDeviceSelectionForm.set(false);
      this.showOnboardingExperience.set(false);
      this.showTutorial.set(false);
      return;
    }

    await this.joinButtonClicked();
  }

  // Join Room Logic
  protected async joinButtonClicked(): Promise<void> {
    const social = this.social();
    if (!social) {
      this.error('Tried to join with invalid social object');
      return;
    }

    this.showLoader.set(true);

    // Ensure devices are set before joining
    this.ensureDefaultDevicesAreSet();

    if (!this.devicePermissionService.cameras().length && !this.isTestUser()) {
      this.info('Joining without a camera present. Muting video');
      this.store.dispatch(mediaActions.MUTE_VIDEO());
    }

    this.store.dispatch(actions.LOAD_SOCIAL({ social: social }));

    this.didClickJoinNow.set(true);
    this.info(`Waiting Room Join Now Social button clicked -- user is host ${this.isHost}`);

    if (!this.demoCompleted() && this.onboardingExperience() && !this.isMobile()) {
      await this.onTutorialCompleted();
    }

    // TODO: review if this logic is still necessary
    //Tutorial Logic
    if (this.isIntroVideoDisabled) {
      this.info('Intro video is disabled by flag, skipping');
      this.showLoader.set(false);
      await this.startJoinSocial(social);
      return;
    }

    if (this.demoCompleted()) {
      // Already completed demo - Skip to room
      this.showLoader.set(false);
      await this.startJoinSocial(social);
      return;
    } else {
      //If the demo has not been completed, show it.
      this.showLoader.set(false);
      if (this.useNewTutorial) {
        // Enable tag for new tutorial
        this.showTutorial.set(true);
        return;
      } else {
        if (this.customRoomTutorial?.tutorial_url) {
          const { subtitle, title, tutorial_url } = this.customRoomTutorial;
          this.showRoomTutorialVideo(tutorial_url, title, subtitle);
          return;
        }
        // In case of url not being configured, skip the custom video and move to room
        await this.startJoinSocial(social);
        return;
      }
    }
  }

  /**
   * Update the host's timezone and locale attributes.
   */
  private async updateHostAttributes() {
    const timezone = new Date().toLocaleTimeString(undefined, { timeZoneName: 'short' }).split(' ').pop();
    const { locale } = Intl.DateTimeFormat().resolvedOptions();

    const userController = new UserController();
    try {
      await userController.setTimeZoneAndLocale({ timezone, locale });
    } catch (e) {
      this.error('Failed to set host attributes', e);
    }
  }

  public ngAfterContentChecked(): void {
    // Called after every check of the component's or directive's content.
    // Necessary to avoid ExpressionChangedAfterItHasBeenCheckedError
    if (!this.connectedRoomsService.selectedConnectedRoom || !this.connectedRoomSet()) {
      return;
    }

    // If max participants and user is not host set isFull
    const selectedRoom = this.connectedRoomsService.selectedConnectedRoom();
    const isFull = selectedRoom ? this.isConnectedRoomFull(selectedRoom) && !this.isHost : false;
    this.selectedRoomFull.set(isFull);
  }

  /**
   * GETS AND SET FLAGS
   *
   * Check if the connected room is full
   */
  private isConnectedRoomFull(room: Social): boolean {
    const roomCount = this.getRoomCount(room) ?? 0;
    const maxParticipants = room.maxParticipants ?? 0;
    return roomCount > maxParticipants - 1;
  }

  /**
   * Get the room count for the selected room
   */
  private getRoomCount(room: Social): number | undefined {
    if (!this.connectedRoomCounts) {
      return;
    }
    const { socialId } = room ?? {};
    if (!socialId) {
      return 0;
    }
    return this.connectedRoomCounts[socialId] ?? 0;
  }

  /**
   * Check if the continue button can be enabled
   */
  protected showPermissionsExplanationModal(): void {
    this.devicePermissionService.showExplanationModal();
  }

  private readonly audioPermissionGranted = computed(() => {
    return !this.devicePermissionService.isAudioPermissionDenied();
  });

  private readonly videoPermissionGranted = computed(() => {
    return !this.devicePermissionService.isVideoPermissionDenied();
  });

  protected readonly showPermissionHelp = computed(() => {
    return !this.videoPermissionGranted() || !this.audioPermissionGranted();
  });

  protected readonly formFooterText = computed(() => {
    if (!this.videoPermissionGranted() && !this.audioPermissionGranted()) {
      return `For the best experience, please configure both Camera and Microphone permissions`;
    } else if (!this.videoPermissionGranted()) {
      return `For the best experience, please configure your Camera permissions`;
    } else if (!this.audioPermissionGranted()) {
      return `For the best experience, please configure your Microphone permissions`;
    }
    return '';
  });

  protected readonly helpLink = computed(() => {
    return `<a href="${HelpCenterUrls.SCOOT_ENABLE_CAMERA_AND_MIC}" target="_blank">Get Help</a>`;
  });

  /**
   * Check if there is a connected room selected
   */
  private readonly noConnectedRoomSelected = computed(() => {
    return this.connectedRoomsService.noConnectedRoomSelected;
  });

  private readonly canJoinOrStart = computed(() => {
    if (this.noConnectedRoomSelected()) {
      return false;
    }
    if (!this.user()) {
      return false;
    }
    return this.roomIsOpen || this.isHost || (this.social()?.anyoneCanStart ?? false);
  });

  /**
   * The text to show in the continue/join button.
   */
  protected readonly joinButtonText = computed(() => {
    if (this.eventsCountdown() && this.eventFlowStep()) {
      return 'Continue';
    }

    if (this.showSmartBadging() && this.smartBadgingHasQuestions()) {
      return 'Continue';
    }
    return 'Enter Room';
  });

  protected readonly isGuest = computed(() => {
    return this.authService.isAnonymous();
  });

  protected readonly isConnected = computed(() => {
    return (this.connectedRoomSet() ?? []).length > 1;
  });

  protected readonly roomName = computed(() => {
    const event = this.event();
    if (!event?.name) {
      return this.connectedRoomSet() && this.connectedRoomSet().length > 1 ? this.scootaverse?.name : this.roomsName;
    } else {
      return event?.name;
    }
  });

  private cleanDeviceDetection(): void {
    if (this.didClickJoinNowTimeout) {
      clearTimeout(this.didClickJoinNowTimeout);
    }
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public ngOnDestroy() {
    this.cleanDeviceDetection();
    this.socialStarted = false;
  }

  protected async finishOnboardingExperience(finishedOnboarding: boolean): Promise<void> {
    if (finishedOnboarding) {
      this.showOnboardingExperience.set(false);
      this.showOrientationStep.set(false);

      if (this.eventsCountdown() && this.eventFlowStep() === 'orientation') {
        this.eventsService.eventFlowStep = undefined;
        await this.redirectToCountdownPage();
      } else {
        await this.joinButtonClicked();
      }
    } else {
      this.badgeQuestionsStep.set(true);
      this.showTutorial.set(false);
      this.showOrientationStep.set(true);
      this.shouldContinueFromSmartBadging.set(!finishedOnboarding);
      void this.playOnboardingAudio();
      await this.continueBehavior();
    }
  }

  private async playOnboardingAudio(): Promise<void> {
    try {
      this.audioElement = document.createElement('audio');
      this.audioElement.src = './assets/video/onboarding-experience/3-4.mp4';

      // Check if audio is already playing
      if (!this.audioElement.paused) {
        this.audioElement.pause();
      } else {
        // Handle the case where default audio devices may not be set
        if (this.devicePermissionService.speakers().length > 0 && !this.devicePermissionService.selectedSpeaker()) {
          // Set the first available speaker as default
          const defaultSpeaker = this.devicePermissionService.speakers()[0];
          this.devicePermissionService.setSpeaker(defaultSpeaker);
          this.info('Setting default speaker device', defaultSpeaker);
        }

        try {
          await this.audioElement.play();
        } catch (error) {
          this.error('Error playing onboarding audio:', error);
          // Silently fail instead of showing alert to users
        }
      }
    } catch (error) {
      this.error('Unexpected error in playOnboardingAudio:', error);
    }
  }

  public clearAudioElement(): void {
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.src = '';
      this.audioElement.currentTime = 0;
    }
  }

  private async redirectToCountdownPage(): Promise<void> {
    if (this.eventsService.eventId) {
      await this.router.navigate([`events/${this.eventsService.eventId}/countdown`]);
    }
  }

  private async handleWelcomeFlowStep(step?: string): Promise<void> {
    switch (step) {
      case 'orientation':
        this.showTutorial.set(true);
        this.showOnboardingExperience.set(true);
        this.showWelcomeDeviceSelectionForm.set(false);
        break;
      case 'badge':
        this.showSmartBadging.set(true);
        this.showWelcomeDeviceSelectionForm.set(false);
        break;
      case 'joinRoom':
        await this.joinButtonClicked();
        break;
      case 'permissions':
      default:
        this.showWelcomeDeviceSelectionForm.set(true);
        break;
    }
    this.cdr.markForCheck();
  }

  private ensureDefaultDevicesAreSet(): void {
    // Set default camera if available
    if (this.devicePermissionService.cameras().length > 0 && !this.devicePermissionService.selectedCamera()) {
      const defaultCamera = this.devicePermissionService.cameras()[0];
      this.info('Setting default camera device', defaultCamera);
      this.devicePermissionService
        .setCamera(defaultCamera)
        .catch((error) => this.error('Failed to set default camera', error));
    }

    // Set default microphone if available
    if (this.devicePermissionService.mics().length > 0 && !this.devicePermissionService.selectedMic()) {
      const defaultMic = this.devicePermissionService.mics()[0];
      this.info('Setting default microphone device', defaultMic);
      this.devicePermissionService
        .setMic(defaultMic)
        .catch((error) => this.error('Failed to set default microphone', error));
    }

    // Set default speaker if available
    if (this.devicePermissionService.speakers().length > 0 && !this.devicePermissionService.selectedSpeaker()) {
      const defaultSpeaker = this.devicePermissionService.speakers()[0];
      this.info('Setting default speaker device', defaultSpeaker);
      this.devicePermissionService.setSpeaker(defaultSpeaker);
    }
  }
}
